/**
 * AskOrSearch — SHARED SOURCE OF TRUTH (Harmony-QMS plan 05-08).
 * COPY (plan 05-10) — source of truth is harmony-qms-fieldlink/src/components/AskOrSearch;
 * do NOT edit here, sync manually from the fieldlink repo.
 * SYNCED 2026-10-06 from fieldlink 6ebb421 (13.1-10 back-port): content matches upstream.
 * Part of the self-contained AskOrSearch folder — see types.ts header.
 *
 * Tiny dependency-free renderer for the Advisor's markdown-ish answers:
 * headings, bold, bullet/numbered lists, table rows and paragraphs. It is
 * deliberately minimal — unknown syntax degrades to plain text, never breaks.
 */

import type { ReactNode } from 'react';

/** Render **bold** spans inside one line of text. */
function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const parts = text.split(/\*\*(.+?)\*\*/g);
  parts.forEach((part, i) => {
    if (!part) return;
    out.push(i % 2 === 1 ? <strong key={i}>{part}</strong> : part);
  });
  return out;
}

interface Block {
  type: 'heading' | 'list' | 'paragraph' | 'rule';
  lines: string[];
}

function toBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let current: Block | null = null;
  const flush = () => {
    if (current && current.lines.length) blocks.push(current);
    current = null;
  };

  for (const raw of String(text || '').split('\n')) {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (!trimmed) {
      flush();
      continue;
    }
    if (/^-{3,}$/.test(trimmed)) {
      flush();
      blocks.push({ type: 'rule', lines: [] });
      continue;
    }
    if (/^#{1,6}\s+/.test(trimmed)) {
      flush();
      blocks.push({ type: 'heading', lines: [trimmed.replace(/^#{1,6}\s+/, '')] });
      continue;
    }
    if (/^(?:[-*•]|\d+[.)])\s+/.test(trimmed)) {
      if (!current || current.type !== 'list') {
        flush();
        current = { type: 'list', lines: [] };
      }
      current.lines.push(trimmed);
      continue;
    }
    if (/^\|.*\|$/.test(trimmed)) {
      // Table rows render as simple lines (cells joined) — small screens.
      const cells = trimmed
        .slice(1, -1)
        .split('|')
        .map((c) => c.trim())
        .filter(Boolean);
      if (cells.every((c) => /^[-:\s]*$/.test(c))) continue; // separator row
      if (!current || current.type !== 'list') {
        flush();
        current = { type: 'list', lines: [] };
      }
      current.lines.push(`- ${cells.join(' — ')}`);
      continue;
    }
    if (!current || current.type !== 'paragraph') {
      flush();
      current = { type: 'paragraph', lines: [] };
    }
    current.lines.push(trimmed);
  }
  flush();
  return blocks;
}

export function AnswerMarkdown({ text }: { text: string }) {
  const blocks = toBlocks(text);
  return (
    <div className="aos-answer__body">
      {blocks.map((block, i) => {
        if (block.type === 'rule') return <hr key={i} className="aos-rule" />;
        if (block.type === 'heading') {
          return (
            <h3 key={i} className="aos-heading">
              {renderInline(block.lines[0])}
            </h3>
          );
        }
        if (block.type === 'list') {
          return (
            <ul key={i} className="aos-list">
              {block.lines.map((line, j) => {
                const numbered = /^(\d+)[.)]\s+(.*)$/.exec(line);
                const item = numbered ? numbered[2] : line.replace(/^[-*•]\s+/, '');
                return (
                  <li key={j} className={numbered ? 'aos-list__item aos-list__item--n' : 'aos-list__item'}>
                    {numbered && <span className="aos-list__num">{numbered[1]}.</span>}
                    <span>{renderInline(item)}</span>
                  </li>
                );
              })}
            </ul>
          );
        }
        return <p key={i}>{renderInline(block.lines.join(' '))}</p>;
      })}
    </div>
  );
}
